import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, Search, CheckCircle2, AlertTriangle, XCircle, HelpCircle,
  ChevronDown, Layers, LayoutGrid, List, ArrowUpDown,
} from 'lucide-react';
import UserAvatar from '../components/UserAvatar';
import ScopeEntityTile from '../components/ScopeEntityTile';
import { useScopeSummary } from '../hooks/useScopeSummary';
import { ApplicationServiceSummary, DiscoveryStatus } from '../services/cmdb/ScopeService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_ORDER: Record<DiscoveryStatus, number> = {
  NotFound: 0,
  Missing:  1,
  Partial:  2,
  Discovered: 3,
};

const STAT_CARDS = [
  {
    key: 'total' as const,
    label: 'In Scope',
    icon: <Layers size={18} />,
    bg: 'bg-[#E6F0F9]',
    text: 'text-[#0054A4]',
    border: 'border-[#0054A4]/20',
  },
  {
    key: 'fullyDiscovered' as const,
    label: 'Fully Discovered',
    icon: <CheckCircle2 size={18} />,
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
  },
  {
    key: 'partiallyDiscovered' as const,
    label: 'Partial Coverage',
    icon: <AlertTriangle size={18} />,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
  },
  {
    key: 'notDiscovered' as const,
    label: 'No Coverage',
    icon: <XCircle size={18} />,
    bg: 'bg-red-50',
    text: 'text-red-600',
    border: 'border-red-200',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function ScopeDashboardPage() {
  const navigate = useNavigate();

  // Support-group input — the user either types it or it is pre-filled from
  // their Azure AD department claim (future: read from AuthContext).
  const [groupInput, setGroupInput]       = useState('');
  const [activeGroup, setActiveGroup]     = useState<string | null>(null);
  const [searchQuery, setSearchQuery]     = useState('');
  const [statusFilter, setStatusFilter]   = useState<DiscoveryStatus | 'all'>('all');
  const [sortBy, setSortBy]               = useState<'name' | 'coverage' | 'status'>('status');
  const [viewMode, setViewMode]           = useState<'grid' | 'list'>('grid');

  const { state, refresh } = useScopeSummary(activeGroup);

  // ── Derived data ──────────────────────────────────────────────────────────

  const summary = state.phase === 'success' ? state.data : null;

  const statCounts = {
    total:              summary?.totalApplicationServices ?? 0,
    fullyDiscovered:    summary?.fullyDiscovered          ?? 0,
    partiallyDiscovered:summary?.partiallyDiscovered      ?? 0,
    notDiscovered:      summary?.notDiscovered            ?? 0,
  };

  const filteredServices = useMemo<ApplicationServiceSummary[]>(() => {
    let list = summary?.services ?? [];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        s.name.toLowerCase().includes(q) || s.applicationId.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== 'all') {
      list = list.filter(s => s.overallStatus === statusFilter);
    }

    return [...list].sort((a, b) => {
      if (sortBy === 'name')     return a.name.localeCompare(b.name);
      if (sortBy === 'coverage') return b.coveragePercent - a.coveragePercent;
      // default: action-priority — least discovered first
      return STATUS_ORDER[a.overallStatus] - STATUS_ORDER[b.overallStatus];
    });
  }, [summary, searchQuery, statusFilter, sortBy]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  function handleLoad() {
    if (groupInput.trim()) setActiveGroup(groupInput.trim());
  }

  function handleOpenInMapper(sysId: string, appId: string) {
    // Navigate to the service mapper and pre-populate the anchor node ID.
    // The Service Mapper uses sessionStorage so the page can pick it up on mount.
    if (appId) sessionStorage.setItem('scope_dashboard_appId', appId);
    if (sysId) sessionStorage.setItem('scope_dashboard_sysId', sysId);
    navigate('/service-mapper');
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-white to-[#E6F0F9] flex flex-col">

      {/* ── Header ── */}
      <header className="w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm z-50">
        <div className="max-w-full mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-sm p-1 border-t-2 border-[#E4002B]">
              <img src="/images/CVX_Logo_NoTagline_RGB.png" alt="Chevron Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <span className="text-sm font-semibold text-[#0054A4]">Service Mapping</span>
              <span className="mx-2 text-gray-300">|</span>
              <span className="text-sm text-gray-500">Scope Dashboard</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/service-mapper')}
              className="text-xs text-[#0054A4] hover:text-[#003A73] font-medium px-3 py-1.5 rounded border border-[#0054A4]/30 hover:bg-[#E6F0F9] transition-colors"
            >
              Open Service Mapper
            </button>
            <UserAvatar />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8">

        {/* ── Page title ── */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Scope Coverage Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Visualise entity discovery coverage for all Application Services in your support group.
          </p>
        </div>

        {/* ── Support-group selector ── */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-8">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Support Group
          </label>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={groupInput}
              onChange={e => setGroupInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLoad()}
              placeholder="e.g. WIPRO-SUBSURFACE-LAND-LAND SOLUTION"
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0054A4]/30 focus:border-[#0054A4]"
            />
            <button
              onClick={handleLoad}
              disabled={!groupInput.trim() || state.phase === 'loading'}
              className="px-5 py-2 bg-[#0054A4] text-white text-sm font-semibold rounded-lg hover:bg-[#003A73] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Load
            </button>
            {activeGroup && (
              <button
                onClick={refresh}
                disabled={state.phase === 'loading'}
                className="p-2 text-gray-400 hover:text-[#0054A4] transition-colors"
                title="Refresh"
              >
                <RefreshCw size={16} className={state.phase === 'loading' ? 'animate-spin' : ''} />
              </button>
            )}
          </div>
          {activeGroup && (
            <p className="text-xs text-gray-400 mt-2">
              Showing data for: <span className="font-semibold text-[#0054A4]">{activeGroup}</span>
              {summary && (
                <span className="ml-2 text-gray-300">
                  · Last fetched {new Date(summary.fetchedAt).toLocaleTimeString()}
                </span>
              )}
            </p>
          )}
        </div>

        {/* ── Loading ── */}
        {state.phase === 'loading' && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-10 h-10 border-4 border-[#0054A4]/20 border-t-[#0054A4] rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Fetching coverage data from ServiceNow CMDB…</p>
          </div>
        )}

        {/* ── Error ── */}
        {state.phase === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <XCircle className="mx-auto mb-2 text-red-500" size={32} />
            <p className="font-semibold text-red-700">Failed to load scope data</p>
            <p className="text-sm text-red-500 mt-1">{state.message}</p>
            <button
              onClick={refresh}
              className="mt-4 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Idle (no group selected yet) ── */}
        {state.phase === 'idle' && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
            <HelpCircle size={40} className="text-gray-300" />
            <p className="text-base font-medium text-gray-400">Enter your Support Group to load the scope.</p>
            <p className="text-sm text-gray-300 max-w-md">
              Your support group determines which Application Service IDs are in scope.
              Enter the full ServiceNow support group name above and click Load.
            </p>
          </div>
        )}

        {/* ── Success ── */}
        {state.phase === 'success' && summary && (
          <>
            {/* ── Stat cards ── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {STAT_CARDS.map(card => (
                <button
                  key={card.key}
                  onClick={() => {
                    if (card.key === 'total') {
                      setStatusFilter('all');
                    } else if (card.key === 'fullyDiscovered') {
                      setStatusFilter('Discovered');
                    } else if (card.key === 'partiallyDiscovered') {
                      setStatusFilter('Partial');
                    } else {
                      setStatusFilter('NotFound');
                    }
                  }}
                  className={`${card.bg} ${card.border} border rounded-xl p-4 text-left hover:shadow-sm transition-shadow`}
                >
                  <div className={`${card.text} mb-1`}>{card.icon}</div>
                  <div className={`text-3xl font-bold ${card.text}`}>{statCounts[card.key]}</div>
                  <div className={`text-xs font-medium ${card.text} opacity-80 mt-0.5`}>{card.label}</div>
                </button>
              ))}
            </div>

            {/* ── Toolbar ── */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search services…"
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0054A4]/30"
                />
              </div>

              {/* Status filter */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value as any)}
                  className="appearance-none pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0054A4]/30"
                >
                  <option value="all">All statuses</option>
                  <option value="Discovered">Fully Discovered</option>
                  <option value="Partial">Partial Coverage</option>
                  <option value="NotFound">Not Found</option>
                  <option value="Missing">Missing</option>
                </select>
                <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>

              {/* Sort */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as any)}
                  className="appearance-none pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0054A4]/30"
                >
                  <option value="status">Sort: Action Priority</option>
                  <option value="coverage">Sort: Coverage %</option>
                  <option value="name">Sort: Name</option>
                </select>
                <ArrowUpDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>

              {/* View toggle */}
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-[#0054A4] text-white' : 'bg-white text-gray-400 hover:bg-gray-50'}`}
                >
                  <LayoutGrid size={15} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-[#0054A4] text-white' : 'bg-white text-gray-400 hover:bg-gray-50'}`}
                >
                  <List size={15} />
                </button>
              </div>

              <span className="text-xs text-gray-400 ml-auto">
                {filteredServices.length} of {summary.totalApplicationServices} services
              </span>
            </div>

            {/* ── Empty filter result ── */}
            {filteredServices.length === 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-10 text-center">
                <p className="text-gray-400 text-sm">No services match your filters.</p>
                <button
                  onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                  className="mt-3 text-xs text-[#0054A4] hover:underline"
                >
                  Clear filters
                </button>
              </div>
            )}

            {/* ── Grid / List ── */}
            {filteredServices.length > 0 && viewMode === 'grid' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filteredServices.map(svc => (
                  <ScopeEntityTile
                    key={svc.sysId}
                    service={svc}
                    onOpen={handleOpenInMapper}
                  />
                ))}
              </div>
            )}

            {filteredServices.length > 0 && viewMode === 'list' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-5 py-3">Service Name</th>
                      <th className="text-left px-4 py-3">App ID</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3">Coverage</th>
                      <th className="text-left px-4 py-3">Entities</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredServices.map(svc => {
                      const statusLabel: Record<DiscoveryStatus, string> = {
                        Discovered: 'Discovered',
                        Partial: 'Partial',
                        NotFound: 'Not Found',
                        Missing: 'Missing',
                      };
                      const statusCls: Record<DiscoveryStatus, string> = {
                        Discovered: 'text-green-700 bg-green-50 border-green-200',
                        Partial:    'text-amber-700 bg-amber-50 border-amber-200',
                        NotFound:   'text-red-600  bg-red-50   border-red-200',
                        Missing:    'text-gray-500 bg-gray-50  border-gray-200',
                      };
                      return (
                        <tr key={svc.sysId} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 font-medium text-[#1E3A5F] truncate max-w-[220px]">{svc.name}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs font-mono truncate max-w-[140px]">{svc.applicationId || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold ${statusCls[svc.overallStatus]}`}>
                              {statusLabel[svc.overallStatus]}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-20 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-[#0054A4] rounded-full"
                                  style={{ width: `${svc.coveragePercent}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500">{svc.coveragePercent}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {svc.coveredEntityTypes}/{svc.totalEntityTypes}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleOpenInMapper(svc.sysId, svc.applicationId)}
                              className="text-xs text-[#0054A4] hover:underline font-semibold"
                            >
                              Open →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
